import { useState } from 'react'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Stack from '@mui/material/Stack'
import IconNode from './IconNode'
import NameForm from './NameForm'
import CreateRefusal from './CreateRefusal'
import useNetwork from '../models/useNetwork'
import Modal from './Modal'
import { useModalClose } from '../hooks/useModalClose'
import { limitRefusal, type LimitRefusal } from '../utilities/limitRefusal'
import { isolationLine, membersMayAddNodesLine } from '../utilities/networkSettings'

type Props = {
  inline?: boolean
  onChange?: (name: string) => void
}

export default function ModalNetwork({ inline, onChange }: Readonly<Props>) {
  const [name, setName] = useState('')
  const [refusal, setRefusal] = useState<Pick<LimitRefusal, 'message' | 'upgradeUrl'> | null>(null)
  // The two settings a network starts with (app#376, docs/PERMISSIONS.md § 5 Network and § 6).
  // The creator becomes the owner and may change isolation later on the network page.
  const [isolated, setIsolated] = useState(false)
  const [membersMayAddNodes, setMembersMayAddNodes] = useState(true)
  const { open, handleClose } = useModalClose('/', inline)
  const { add } = useNetwork()
  const defaultName = 'default'

  const handleSubmit = async (value: string) => {
    setName(value)
    setRefusal(null)
    try {
      await add({ name: value ?? defaultName }, { isolated, membersMayAddNodes })
    } catch (e) {
      // ⚠ A spent allowance is rendered in the router's own words; anything else — a name clash,
      // a role refusal — keeps its message and gets no Upgrade action, because upgrading would
      // not lift it. The dialog stays open either way, with the typed name intact (app#296).
      console.error('network_create failed', e)
      setRefusal(limitRefusal(e) ?? { message: e instanceof Error ? e.message : String(e) })
      return
    }
    await handleClose()
  }

  const handleChange = (name: string) => {
    setName(name)
    onChange?.(name)
  }

  return (
    <Modal
      open={open}
      inline={inline}
      maxWidth="sm"
      onClose={inline ? undefined : handleClose}
      color="primary.main"
      icon={<IconNode type="network" width={54} height={54} outline plus />}
    >
      <DialogContent>
        <Typography
          variant="subtitle1"
          color="primary"
          sx={{
            flexGrow: 1,
            mb: 3
          }}>
          New Network
        </Typography>
        <CreateRefusal message={refusal?.message} upgradeUrl={refusal?.upgradeUrl} />
        <NameForm
          autoFocus
          label="Network Name"
          placeholder={defaultName}
          value={name}
          onSubmit={handleSubmit}
          onChange={handleChange}
          lowercase
        />
        {/* ⚠ Not in the onboarding step (`inline`): its own Next button creates the network, with
            the defaults, and a first network has nobody else to keep apart. */}
        {!inline && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={isolated} onChange={e => setIsolated(e.target.checked)} />}
              label="Client isolation"
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1 }}>
              {isolationLine(isolated)}
            </Typography>
            <FormControlLabel
              control={<Switch checked={membersMayAddNodes} onChange={e => setMembersMayAddNodes(e.target.checked)} />}
              label="Members may add nodes"
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1 }}>
              {membersMayAddNodesLine(membersMayAddNodes)}
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Modal>
  )
}
